import type { StoreName } from './entities';

/**
 * The storage backend, reduced to the few operations the application needs.
 *
 * Two implementations exist: IndexedDB for the running application, and an
 * in-memory one for tests. Because the repositories are written against this
 * interface rather than against IndexedDB, the isolation and sealing logic can
 * be tested exhaustively without a browser, and swapping to SQLite behind
 * Tauri later would touch only this file.
 */
export interface RecordStore {
  get(store: StoreName, id: string): Promise<unknown>;
  put(store: StoreName, id: string, value: unknown): Promise<void>;
  delete(store: StoreName, id: string): Promise<void>;
  all(store: StoreName): Promise<readonly { readonly id: string; readonly value: unknown }[]>;
  clear(store: StoreName): Promise<void>;
  close(): void;
}

/**
 * In-memory backend.
 *
 * Used by the test suite, and as the fallback when IndexedDB is unavailable,
 * which happens in some private browsing modes. Falling back keeps the
 * application usable for the session rather than failing to start, at the cost
 * of not persisting, which the interface surfaces rather than hides.
 */
export class MemoryRecordStore implements RecordStore {
  readonly #data = new Map<StoreName, Map<string, unknown>>();

  #bucket(store: StoreName): Map<string, unknown> {
    let bucket = this.#data.get(store);
    if (bucket === undefined) {
      bucket = new Map();
      this.#data.set(store, bucket);
    }
    return bucket;
  }

  async get(store: StoreName, id: string): Promise<unknown> {
    return structuredCloneSafe(this.#bucket(store).get(id));
  }

  async put(store: StoreName, id: string, value: unknown): Promise<void> {
    // Cloned on write so a caller mutating the object it passed in cannot
    // silently change what is stored, matching how IndexedDB behaves.
    this.#bucket(store).set(id, structuredCloneSafe(value));
  }

  async delete(store: StoreName, id: string): Promise<void> {
    this.#bucket(store).delete(id);
  }

  async all(store: StoreName) {
    return [...this.#bucket(store).entries()].map(([id, value]) => ({
      id,
      value: structuredCloneSafe(value),
    }));
  }

  async clear(store: StoreName): Promise<void> {
    this.#bucket(store).clear();
  }

  close(): void {
    this.#data.clear();
  }
}

function structuredCloneSafe<T>(value: T): T {
  if (value === undefined || value === null) return value;
  if (typeof structuredClone === 'function') return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
}
